import React, { useState, useEffect } from 'react';
import { useGameStore } from '../../store/gameStore';
import { getMenuByPeriod, getMealPeriodEmoji, getComplexityColor, MenuItem } from '../../data/menuData';
import { getCharacterSprite } from '../../data/npcData';
import { getCustomerCharacter, calculatePreferenceMatch, getCharacterReaction } from '../../data/characterData';
import { TableSettingGame } from './TableSettingGame';

interface Props {
  send: (action: string, data?: object) => void;
}

function calculateAutoRating(
  characterId: string | null, 
  itemFlavors: string[], 
  waitSeconds: number, 
  targetMinutes: number,
  cookingQuality: number = 70 // Chef's timing performance (0-100)
): { 
  rating: number; 
  preferenceScore: number; 
  timeScore: number; 
  qualityScore: number;
  totalPoints: number;
  preferenceMatch: ReturnType<typeof calculatePreferenceMatch>;
} {
  const targetSeconds = targetMinutes * 60;
  
  // Preference score (0-35 based on flavor matching)
  const preferenceMatch = calculatePreferenceMatch(characterId, itemFlavors);
  const preferenceScore = Math.round((preferenceMatch.score / 100) * 35); // 0-35
  
  // Time score (0-30 based on wait time)
  let timeScore: number;
  if (waitSeconds <= targetSeconds * 0.7) {
    timeScore = 30; // Super fast
  } else if (waitSeconds <= targetSeconds) {
    timeScore = 25; // On time
  } else if (waitSeconds <= targetSeconds * 1.3) {
    timeScore = 18; // Slightly late
  } else if (waitSeconds <= targetSeconds * 2) {
    timeScore = 10; // Late
  } else {
    timeScore = 3; // Very late
  }
  
  // Quality score from chef's timing (0-35 based on cooking quality)
  const qualityScore = Math.round((cookingQuality / 100) * 35); // 0-35
  
  // Total and rating (1-5 stars)
  const totalPoints = preferenceScore + timeScore + qualityScore;
  const rating = Math.min(5, Math.max(1, Math.round((totalPoints / 100) * 5)));
  
  return { rating, preferenceScore, timeScore, qualityScore, totalPoints, preferenceMatch };
}

export function CustomerView({ send }: Props) {
  const { currentMealPeriod, orders, playerId, playerName, characterId } = useGameStore();
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [isOrdering, setIsOrdering] = useState(false);
  const [waitTime, setWaitTime] = useState(0);
  const [tableGameBonus, setTableGameBonus] = useState(0); // Bonus from table setting mini-game
  const [deliveryResult, setDeliveryResult] = useState<{
    rating: number;
    preferenceScore: number;
    timeScore: number;
    qualityScore: number;
    tableBonus: number;
    totalPoints: number;
    reaction: string;
    preferenceMatch: ReturnType<typeof calculatePreferenceMatch>;
  } | null>(null);
  
  const mealPeriod = currentMealPeriod || 'breakfast';
  const menu = getMenuByPeriod(mealPeriod);
  const emoji = getMealPeriodEmoji(mealPeriod);
  
  // Get customer character data
  const customerCharacter = getCustomerCharacter(characterId);
  
  // Check if player already has an active order this period
  const myOrders = orders.filter(o => o.customerId === playerId);
  const activeOrder = myOrders.find(o => o.status !== 'delivered');
  const hasOrdered = !!activeOrder;
  
  // Live wait time counter
  useEffect(() => {
    if (!activeOrder) {
      setWaitTime(0);
      return;
    }
    
    setWaitTime(Math.floor((Date.now() - activeOrder.createdAt) / 1000));
    
    const timer = setInterval(() => {
      setWaitTime(Math.floor((Date.now() - activeOrder.createdAt) / 1000));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [activeOrder?.id, activeOrder?.createdAt]);

  const handleOrder = (item: MenuItem) => {
    if (isOrdering || hasOrdered) return;
    
    setIsOrdering(true);
    setDeliveryResult(null);
    
    send('placeOrder', { menuItemId: item.id });
    setSelectedItem(null);
    
    setTimeout(() => {
      setIsOrdering(false);
    }, 500);
  };

  const formatWaitTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-rate when order is delivered
  const deliveredOrder = myOrders.find(o => o.status === 'delivered');
  
  useEffect(() => {
    if (deliveredOrder && !deliveryResult) {
      const menuItem = menu.find(m => m.id === deliveredOrder.menuItemId);
      if (menuItem) {
        const targetMinutes = menuItem.prepTime || 3;
        const waitSeconds = Math.floor((Date.now() - deliveredOrder.createdAt) / 1000);
        // Get cooking quality from the order (set by chef's timing minigame)
        const cookingQuality = deliveredOrder.cookingQuality ?? 70; // Default 70 if not set
        const result = calculateAutoRating(characterId, menuItem.flavors, waitSeconds, targetMinutes, cookingQuality);
        // Add table game bonus to total (cap at 30 extra points)
        const cappedTableBonus = Math.min(tableGameBonus, 30);
        const adjustedTotal = result.totalPoints + cappedTableBonus;
        const adjustedRating = Math.min(5, Math.max(1, Math.round((adjustedTotal / 130) * 5)));
        const reaction = getCharacterReaction(characterId, adjustedRating);
        setDeliveryResult({ 
          ...result, 
          tableBonus: cappedTableBonus,
          totalPoints: adjustedTotal,
          rating: adjustedRating,
          reaction 
        });
        // Reset table bonus for next order
        setTableGameBonus(0);
      }
    }
  }, [deliveredOrder?.id, deliveredOrder?.cookingQuality, deliveryResult, menu, characterId, tableGameBonus]);
  
  // Show delivery result (auto-rated)
  if (deliveredOrder && !activeOrder && deliveryResult) {
    const deliveredItem = menu.find(m => m.id === deliveredOrder.menuItemId);
    const totalWait = Math.floor((Date.now() - deliveredOrder.createdAt) / 1000);
    const characterImage = getCharacterSprite(characterId || 'girl_1');
    
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <div style={{
          background: deliveryResult.rating >= 4 ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' :
                      deliveryResult.rating >= 3 ? 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)' :
                      'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
          border: `4px solid ${deliveryResult.rating >= 4 ? '#4caf50' : deliveryResult.rating >= 3 ? '#ff9800' : '#f44336'}`,
          padding: 28,
          maxWidth: 520,
          margin: '0 auto',
        }}>
          <h2 style={{ 
            color: deliveryResult.rating >= 4 ? '#2e7d32' : deliveryResult.rating >= 3 ? '#e65100' : '#c62828', 
            marginBottom: 20, 
            fontSize: 24 
          }}>
            🍽️ Order Delivered!
          </h2>
          
          {/* Character reaction speech bubble */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: 12, 
            marginBottom: 20,
            background: 'white',
            padding: 16,
            border: '3px solid #333',
          }}>
            <div style={{
              width: 72,
              height: 72,
              backgroundImage: `url(${characterImage})`,
              backgroundSize: 'auto 100%',
              backgroundPosition: 'left center',
              backgroundRepeat: 'no-repeat',
              imageRendering: 'pixelated',
              flexShrink: 0,
            }} />
            <div style={{ 
              flex: 1, 
              textAlign: 'left',
              background: '#f5f5f5',
              padding: 14,
              borderRadius: 10,
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                left: -8,
                top: 16,
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderRight: '8px solid #f5f5f5',
              }} />
              <div style={{ fontWeight: 600, marginBottom: 4, color: '#333', fontSize: 13 }}>
                {customerCharacter?.name || playerName}
              </div>
              <div style={{ fontSize: 16, color: '#333', lineHeight: 1.4 }}>
                "{deliveryResult.reaction}"
              </div>
            </div>
          </div>
          
          {/* Food + star rating */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 16, 
            marginBottom: 20,
            padding: 16,
            background: 'white',
            border: '3px solid #8b4513',
          }}>
            <img 
              src={deliveredItem?.image || '/images/food/breakfast/pancakes_dish.png'}
              alt={deliveredItem?.name}
              style={{ width: 56, height: 56, imageRendering: 'pixelated' }}
            />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#8b4513' }}>{deliveredItem?.name}</div>
              <div style={{ fontSize: 28, marginTop: 4 }}>
                {Array(5).fill(0).map((_, i) => (
                  <span key={i} style={{ 
                    opacity: i < deliveryResult.rating ? 1 : 0.25,
                  }}>⭐</span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Preference match breakdown */}
          <div style={{
            background: 'white',
            border: '2px solid #333',
            padding: 12,
            marginBottom: 16,
            textAlign: 'left',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
              🎯 Flavor Match ({deliveryResult.preferenceMatch.matchType.toUpperCase()})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {deliveryResult.preferenceMatch.matchedFlavors.length > 0 ? (
                deliveryResult.preferenceMatch.matchedFlavors.map((f, i) => (
                  <span key={i} style={{
                    background: f.startsWith('❤️') ? '#e8f5e9' : f.startsWith('👍') ? '#e3f2fd' : '#ffebee',
                    border: `1px solid ${f.startsWith('❤️') ? '#4caf50' : f.startsWith('👍') ? '#2196f3' : '#f44336'}`,
                    padding: '2px 8px',
                    fontSize: 12,
                  }}>
                    {f}
                  </span>
                ))
              ) : (
                <span style={{ color: '#999', fontSize: 12 }}>No strong preference match</span>
              )}
            </div>
          </div>
          
          {/* Score breakdown */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 6,
            marginBottom: 20,
          }}>
            <div style={{ background: 'white', padding: 8, border: '2px solid #333' }}>
              <div style={{ fontSize: 9, color: '#666' }}>Wait</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#ff9800' }}>{formatWaitTime(totalWait)}</div>
            </div>
            <div style={{ background: 'white', padding: 8, border: '2px solid #333' }}>
              <div style={{ fontSize: 9, color: '#666' }}>Taste</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#9c27b0' }}>+{deliveryResult.preferenceScore}</div>
            </div>
            <div style={{ background: 'white', padding: 8, border: '2px solid #333' }}>
              <div style={{ fontSize: 9, color: '#666' }}>Speed</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#4682b4' }}>+{deliveryResult.timeScore}</div>
            </div>
            <div style={{ 
              background: deliveryResult.qualityScore >= 30 ? '#e8f5e9' : deliveryResult.qualityScore >= 20 ? '#fff8e1' : '#ffebee', 
              padding: 8, 
              border: `2px solid ${deliveryResult.qualityScore >= 30 ? '#4caf50' : deliveryResult.qualityScore >= 20 ? '#ff9800' : '#f44336'}` 
            }}>
              <div style={{ fontSize: 9, color: '#666' }}>Cooking</div>
              <div style={{ 
                fontSize: 13, 
                fontWeight: 700, 
                color: deliveryResult.qualityScore >= 30 ? '#4caf50' : deliveryResult.qualityScore >= 20 ? '#ff9800' : '#f44336' 
              }}>
                +{deliveryResult.qualityScore}
              </div>
            </div>
            <div style={{ 
              background: deliveryResult.tableBonus > 0 ? '#e3f2fd' : '#f5f5f5', 
              padding: 8, 
              border: `2px solid ${deliveryResult.tableBonus > 0 ? '#2196f3' : '#999'}` 
            }}>
              <div style={{ fontSize: 9, color: '#666' }}>Table 🍽️</div>
              <div style={{ 
                fontSize: 13, 
                fontWeight: 700, 
                color: deliveryResult.tableBonus > 0 ? '#1976d2' : '#999'
              }}>
                +{deliveryResult.tableBonus}
              </div>
            </div>
          </div>
          
          {/* Total */}
          <div style={{
            background: deliveryResult.rating >= 4 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
                        deliveryResult.rating >= 3 ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' :
                        'linear-gradient(135deg, #434343 0%, #000000 100%)',
            color: 'white',
            padding: 14,
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 12, opacity: 0.9 }}>Total Points</div>
            <div style={{ fontSize: 32, fontWeight: 700 }}>+{deliveryResult.totalPoints}</div>
          </div>
          
          <button
            onClick={() => {
              useGameStore.setState(state => ({
                orders: state.orders.filter(o => o.id !== deliveredOrder.id)
              }));
              setDeliveryResult(null);
            }}
            style={{
              background: '#4caf50',
              color: 'white',
              border: 'none',
              padding: '14px 40px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🍽️ ORDER AGAIN
          </button>
        </div>
      </div>
    );
  }

  // Waiting for order screen
  if (hasOrdered && activeOrder) {
    const orderedItem = menu.find(m => m.id === activeOrder.menuItemId);
    const queuePosition = orders.filter(o => o.status === 'pending').findIndex(o => o.id === activeOrder.id) + 1;
    const targetSeconds = (orderedItem?.prepTime || 3) * 60;
    const isOvertime = waitTime > targetSeconds;
    
    const handleTableGameComplete = (score: number) => {
      setTableGameBonus(prev => prev + score);
    };
    
    return (
      <div style={{ padding: 24, maxWidth: 520, margin: '0 auto' }}>
        <h2 style={{ 
          fontFamily: 'var(--ltc-font-display)', 
          color: 'var(--ltc-primary)',
          fontSize: 24,
          marginBottom: 8,
          textAlign: 'center',
        }}>
          Your Order is Being Prepared
        </h2>
        <p style={{ color: 'var(--ltc-text-secondary)', marginBottom: 24, textAlign: 'center' }}>
          While you wait, set your table for bonus points!
        </p>
        
        {/* Order Status Card */}
        <div style={{
          background: 'var(--ltc-bg-card)',
          border: 'var(--ltc-border)',
          padding: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <img 
              src={orderedItem?.image || '/images/food/breakfast/pancakes_dish.png'}
              alt="Your order"
              style={{ width: 64, height: 64, imageRendering: 'pixelated' }}
            />
            <div style={{ flex: 1 }}>
              <h3 style={{ color: 'var(--ltc-text-primary)', margin: 0, fontSize: 16 }}>
                {orderedItem?.name || 'Your Order'}
              </h3>
              <div style={{ 
                fontSize: 13, 
                fontWeight: 600, 
                marginTop: 4,
                color: activeOrder.status === 'cooking' ? '#ff9800' : 
                       activeOrder.status === 'completed' ? '#4caf50' : '#4682b4',
              }}>
                {activeOrder.status === 'cooking' ? '👨‍🍳 Cooking now...' : 
                 activeOrder.status === 'completed' ? '✅ Ready for pickup!' : '⏳ In queue...'}
              </div>
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gap: 8,
            padding: 12,
            background: 'var(--ltc-bg-secondary)',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--ltc-text-muted)' }}>Queue</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#4682b4' }}>
                #{queuePosition > 0 ? queuePosition : '-'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--ltc-text-muted)' }}>Wait</div>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 600, 
                color: isOvertime ? '#f44336' : '#ff9800',
              }}>
                {formatWaitTime(waitTime)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--ltc-text-muted)' }}>Target</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#4682b4' }}>
                {orderedItem?.prepTime || 3}:00
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--ltc-text-muted)' }}>Bonus</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#4caf50' }}>
                +{tableGameBonus}
              </div>
            </div>
          </div>
        </div>
        
        {/* Table Setting Mini-Game */}
        <TableSettingGame 
          onComplete={handleTableGameComplete}
          bonusEarned={tableGameBonus}
        />
        
        <p style={{ 
          marginTop: 16, 
          color: 'var(--ltc-text-muted)', 
          fontSize: 12,
          textAlign: 'center',
          fontStyle: 'italic',
        }}>
          💡 Keep playing to earn more bonus points while you wait!
        </p>
      </div>
    );
  }

  // Menu browsing view with character preferences shown
  return (
    <div>
      {/* Character preferences banner */}
      {customerCharacter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: 16,
          marginBottom: 16,
          background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
          border: '3px solid #ff9800',
        }}>
          <div style={{
            width: 64,
            height: 64,
            backgroundImage: `url(${customerCharacter.image})`,
            backgroundSize: 'auto 100%',
            backgroundPosition: 'left center',
            backgroundRepeat: 'no-repeat',
            imageRendering: 'pixelated',
            flexShrink: 0,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#8b4513', marginBottom: 4 }}>
              {customerCharacter.name}'s Preferences
            </div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: '#4caf50' }}>❤️ Loves:</span> {customerCharacter.lovedFlavors.join(', ')}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              <span style={{ color: '#f44336' }}>👎 Dislikes:</span> {customerCharacter.dislikedFlavors.join(', ')}
            </div>
          </div>
        </div>
      )}

      {/* Menu Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 24,
        padding: 16,
        background: 'var(--ltc-bg-secondary)',
        border: 'var(--ltc-border)',
      }}>
        <h2 style={{ 
          fontFamily: 'var(--ltc-font-display)', 
          color: 'var(--ltc-primary)',
          fontSize: 20,
          marginBottom: 4,
        }}>
          {emoji} {mealPeriod.charAt(0).toUpperCase() + mealPeriod.slice(1)} Menu
        </h2>
        <p style={{ color: 'var(--ltc-text-muted)', fontSize: 14 }}>
          Order food that matches your preferences for higher scores!
        </p>
      </div>

      {/* Menu Items with preference indicators */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {menu.map((item) => {
          const prefMatch = calculatePreferenceMatch(characterId, item.flavors);
          const matchColor = prefMatch.matchType === 'loved' ? '#4caf50' : 
                            prefMatch.matchType === 'liked' ? '#2196f3' : 
                            prefMatch.matchType === 'disliked' ? '#f44336' : '#999';
          
          return (
            <div
              key={item.id}
              onClick={() => setSelectedItem(item)}
              style={{
                background: selectedItem?.id === item.id ? '#fff8dc' : 'var(--ltc-bg-card)',
                border: selectedItem?.id === item.id ? '3px solid #8b4513' : 'var(--ltc-border)',
                borderLeft: `5px solid ${matchColor}`,
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <img 
                src={item.image}
                alt={item.name}
                style={{ width: 64, height: 64, imageRendering: 'pixelated', border: '2px solid var(--ltc-border-color)' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: 'var(--ltc-text-primary)' }}>{item.name}</span>
                  {prefMatch.matchType === 'loved' && <span style={{ fontSize: 12, color: '#4caf50' }}>❤️ Perfect match!</span>}
                  {prefMatch.matchType === 'disliked' && <span style={{ fontSize: 12, color: '#f44336' }}>⚠️ Not your taste</span>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ltc-text-secondary)', marginBottom: 8 }}>{item.description}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {item.flavors.map(flavor => {
                    const isLoved = customerCharacter?.lovedFlavors.includes(flavor);
                    const isDisliked = customerCharacter?.dislikedFlavors.includes(flavor);
                    return (
                      <span key={flavor} style={{
                        background: isLoved ? '#e8f5e9' : isDisliked ? '#ffebee' : '#f5f5f5',
                        color: isLoved ? '#2e7d32' : isDisliked ? '#c62828' : '#666',
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 500,
                        border: `1px solid ${isLoved ? '#4caf50' : isDisliked ? '#f44336' : '#ddd'}`,
                      }}>
                        {isLoved && '❤️ '}{isDisliked && '👎 '}{flavor}
                      </span>
                    );
                  })}
                  <span style={{
                    background: '#fff3e0',
                    color: getComplexityColor(item.complexity),
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 500,
                  }}>
                    {item.complexity} ({item.prepTime}min)
                  </span>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrder(item);
                }}
                disabled={isOrdering}
                style={{
                  background: isOrdering ? '#ccc' : 'var(--ltc-accent)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  fontWeight: 600,
                  cursor: isOrdering ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                }}
              >
                {isOrdering ? 'ORDERING...' : 'ORDER'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
